import { Alert, Stack, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import type { LegalDocumentFormValues } from 'src/pages/Settings/memberships/components/legal-document/types';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';

interface LegalDocumentFieldsProps {
  documentOptions: SelectMenuOption[];
  selectedDocumentId: string;
  onDocumentChange: (documentId: string) => void;
  sharedWith: string[];
  domainWarning?: string;
}

export const LegalDocumentFields = ({
  documentOptions,
  selectedDocumentId,
  onDocumentChange,
  sharedWith,
  domainWarning,
}: LegalDocumentFieldsProps) => {
  const { control } = useFormContext<LegalDocumentFormValues>();

  return (
    <Stack p={2} gap={3}>
      <Stack spacing={1}>
        <Typography variant="caption" sx={panelFieldLabelSx}>
          Version
        </Typography>
        <SelectMenu
          label="Version"
          options={documentOptions}
          value={selectedDocumentId}
          onChange={onDocumentChange}
          menuWidth="trigger"
          sx={{ width: '100%' }}
        />
        {sharedWith.length ? (
          <Typography variant="caption" color="text.secondary">
            Also used by {sharedWith.join(', ')}. Saving changes it for all of them.
          </Typography>
        ) : null}
      </Stack>

      <Controller
        name="name"
        control={control}
        rules={{ required: 'Enter a name.' }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Name"
            size="small"
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message ?? 'Shown to your team in the document list.'
            }
          />
        )}
      />

      <Controller
        name="title"
        control={control}
        rules={{ required: 'Enter a title.' }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Title"
            size="small"
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? 'Shown at the top of the page.'}
          />
        )}
      />

      {domainWarning ? <Alert severity="warning">{domainWarning}</Alert> : null}
    </Stack>
  );
};
